import React, { useState, useEffect } from 'react';

import { LinkProps } from 'next/link';
import { useRouter } from 'next/router';

import { AxiosError } from 'axios';
import { GetStaticPaths, GetStaticProps, InferGetStaticPropsType, NextPage } from 'next';
import qs from 'qs';
import styled from 'styled-components';

import { fetchOrgDocumentsV2, withOrg } from 'api/organizations';
import GridPreloader from 'components/Loaders/GridPreloader';
import { MetaTags } from 'components/MetaTags';
import Paging from 'components/Paging';
import PosterGrid from 'components/PosterGrid';
import SearchAndFilters from 'components/SearchAndFilters';
import * as ContainerStyles from 'containers/styles';
import { IError, handleAxiosError } from 'utils/handleAxiosError';
import { isObjectId } from 'utils/validations';

export const SearchContainer = styled.div`
  margin: 1rem 0;
`;

type Props = InferGetStaticPropsType<typeof getStaticProps>;

const limit = 24;
const OrganizationDocuments: NextPage<Props> = ({ data, error }: Props) => {
  const router = useRouter();

  const [isRouteQueryParsed, setIsRouteQueryParsed] = useState(false);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [total, setTotal] = useState<number>(data?.documents.collection?.total ?? 0);
  const [offset, setOffset] = useState<number>(0);
  const [items, setItems] = useState(data?.documents.items ?? []);
  const [searchQuery, setSearchQuery] = useState<string | null>(null);

  const page = Math.ceil(offset / limit) + 1;

  const numberOfPages = data?.documents.collection?.number_of_pages ?? 0;
  useEffect(() => {
    if (isRouteQueryParsed) return;

    const queryInput = qs.parse(window.location.search.slice(1));
    if (queryInput.search) {
      setSearchQuery(queryInput.search as string);
    }
    setIsRouteQueryParsed(true);
    if (typeof queryInput.page !== 'string') return;
    if (parseInt(queryInput.page, 10) > 0) {
      const newOffset = (parseInt(queryInput.page, 10) - 1) * limit;
      setOffset(newOffset);
    }
  }, [router.asPath]);

  useEffect(() => {
    const fetch = async () => {
      setIsLoading(true);
      type PageQuery = {
        search?: string;
        offset: number;
        limit: number;
      };
      let query: PageQuery = { offset, limit };
      if (searchQuery && searchQuery?.length > 0) {
        query.search = searchQuery;
      }
      if (Object.keys(filters).length > 0) {
        query = { ...query, ...filters };
      }

      if (!data) return;

      try {
        const resp = await fetchOrgDocumentsV2({
          organizationId: data.organization.id,
          query,
        });
        setItems(resp.items);
        setTotal(resp.collection?.total);
        setIsLoading(false);
      } catch (err) {
        console.log('fetchOrgDocumentsV2.err:', err);
      }

      type RouterQuery = {
        organization_id: string;
        search?: string;
        page?: number;
      };
      const routerQuery: RouterQuery = {
        organization_id: data.organization.id,
      };
      if (page > 1) {
        routerQuery.page = page;
      }
      if (searchQuery && searchQuery?.length > 0) {
        routerQuery.search = searchQuery;
      }
      router.replace(
        {
          pathname: '/organizations/[organization_id]/documents',
          query: routerQuery,
        },
        undefined,
        { shallow: true },
      );
    };
    // prevent multiple fetches before the route url parsing is complete
    if (isRouteQueryParsed) {
      fetch();
    }
  }, [filters, searchQuery, isRouteQueryParsed, limit, offset]);

  // redirect to new CL UI
  useEffect(() => {
    const { organization_id } = router.query;
    document.location.href = `${
      document.location.href.split('/o/')[0]
    }/cl/organizations/${organization_id}/documents`;
  }, []);

  const handleSearchSubmit = (eventQuery: string | null) => {
    setOffset(0);
    setSearchQuery(eventQuery ?? '');
  };

  const handleQueryFilters = (queryFilters: Record<string, string>) => {
    if (!isLoading) {
      setFilters({ ...queryFilters });
    }
  };

  const pageRouteBuilder = (pageNumber: number): LinkProps['href'] => ({
    pathname: '/organizations/[organization_id]/documents',
    query: {
      organization_id: data?.organization.id,
      page: pageNumber,
      //    filters: queryFilters
    },
  });

  if (!data) {
    return <div>loading...</div>;
  }

  if (error) {
    return (
      <div>
        {error.code} | {error.message}{' '}
      </div>
    );
  }
  return (
    <>
      <MetaTags title={`Documents | ${data.organization.name}`} />

      <ContainerStyles.PageContainer withTopMargin>
        <ContainerStyles.PageTitle>Presentations</ContainerStyles.PageTitle>

        <SearchContainer>
          <SearchAndFilters
            organizationId={data.organization.id}
            searchQuery={searchQuery || ''}
            filters={filters}
            onSearchSubmit={handleSearchSubmit}
            onQueryFiltersChange={handleQueryFilters}
            total={total}
            submissionType="PRESENTATION"
          />
        </SearchContainer>
        {isLoading && <GridPreloader />}
        {!isLoading && data && (
          <>
            <PosterGrid items={items} submissionType="PRESENTATION" showThumbnail={false} />
            <Paging
              currentPage={Number(page)}
              totalPages={Math.min(numberOfPages, Math.ceil(total / limit))}
              pageRouteBuilder={pageRouteBuilder}
            />
          </>
        )}
      </ContainerStyles.PageContainer>
    </>
  );
};

export const getStaticProps: GetStaticProps<
  {
    data?: { documents: OrganizationDocumentsResponse; organization: MorressierOrganization };
    error?: IError;
  },
  { organization_id: string }
> = async ctx => {
  const organizationId = ctx.params?.organization_id;

  try {
    if (!isObjectId(organizationId)) throw new Error(`Invalid organization ID: ${organizationId}`);

    const [organization, documents] = await withOrg(organizationId, () =>
      fetchOrgDocumentsV2({
        organizationId,
        query: {
          offset: 0,
          limit,
        },
      }),
    );

    return {
      props: { data: { organization, documents } },
      revalidate: 60,
    };
  } catch (err) {
    const error = handleAxiosError(err as AxiosError);

    return {
      props: { error },
      revalidate: 60,
    };
  }
};

export const getStaticPaths: GetStaticPaths = async () => ({ paths: [], fallback: 'blocking' });

export default OrganizationDocuments;
